/**
 * URL builder for AutoRender image URLs
 */

import { buildTransformString } from './transform';
import type { TransformOptions } from './types';

const ABSOLUTE_URL_RE = /^https?:\/\//i;

/**
 * Escape only the structural characters of a remote fetch payload:
 * `%` (round-trip safety), `,` (transform-token delimiter), `?` and `#`
 * (terminate the path when the generated URL is requested). Slashes and
 * colons stay literal, so URLs without these characters are unchanged.
 * Kept byte-identical with `@autorender/native`'s escapeFetchPayload —
 * enforced by the native package's URL parity tests.
 */
export function escapeFetchPayload(url: string): string {
  return url
    .replace(/%/g, '%25')
    .replace(/,/g, '%2C')
    .replace(/\?/g, '%3F')
    .replace(/#/g, '%23');
}

/**
 * Workspace path segment for `ar.url()` / ARImage `src`.
 *
 * - Relative paths (`products/shoe.jpg`): each path segment is encoded separately.
 * - Absolute URLs (`https://demo.com/photo.jpg`): emitted as `fetch_<url>`.
 */
export function encodeAssetPathForDelivery(src: string): string {
  const trimmed = src.trim();
  if (!trimmed) {
    return '';
  }

  if (ABSOLUTE_URL_RE.test(trimmed)) {
    const fetchPayload = `fetch_${trimmed}`;
    return encodeURIComponent(fetchPayload);
  }

  if (trimmed.startsWith('fetch_')) {
    return encodeURIComponent(trimmed);
  }

  return trimmed
    .split('/')
    .filter(Boolean)
    .map(segment => encodeURIComponent(segment))
    .join('/');
}

export function buildImageUrl(
  baseUrl: string,
  workspace: string,
  src: string,
  transform?: TransformOptions,
  defaults?: { f?: string; q?: string | number; [key: string]: any },
  // Defaults to `false` for the same reason `buildTransformString` does — a
  // device-derived `dpr_` makes the URL differ between server and client render.
  enableDPR: boolean = false
): string {
  // Normalize base URL (remove trailing slashes)
  const normalizedBase = baseUrl.replace(/\/+$/, '');
  
  // Build transformation string
  const transformStr = buildTransformString(transform || {}, defaults, enableDPR);

  const trimmedSrc = src.trim();

  // Remote fetch support:
  // For `https://...` sources, the backend expects the `fetch_...` token to be part
  // of the transformation segment (comma-separated), not a separate path segment.
  if (ABSOLUTE_URL_RE.test(trimmedSrc)) {
    const fetchPart = `fetch_${escapeFetchPayload(trimmedSrc)}`;
    const transformWithFetch = transformStr ? `${transformStr},${fetchPart}` : fetchPart;
    return `${normalizedBase}/${workspace}/${transformWithFetch}`;
  }

  // Support callers passing an already-prefixed fetch token.
  if (trimmedSrc.startsWith('fetch_')) {
    const fetchPart = `fetch_${escapeFetchPayload(trimmedSrc.slice(6))}`;
    const transformWithFetch = transformStr ? `${transformStr},${fetchPart}` : fetchPart;
    return `${normalizedBase}/${workspace}/${transformWithFetch}`;
  }

  const encodedPath = encodeAssetPathForDelivery(trimmedSrc);
  
  // Build URL: baseUrl/workspace/transformations/file_path
  if (transformStr) {
    return `${normalizedBase}/${workspace}/${transformStr}/${encodedPath}`;
  }

  return `${normalizedBase}/${workspace}/${encodedPath}`;
}

